import type { QueryClient } from "@tanstack/react-query";

import { resolveRecordOpenPath } from "~/config/routes/routeResolution";
// eslint-disable-next-line no-restricted-imports -- navigation must not import the businessObjects barrel
import {
  extractDataRecord,
  isSoftDeletedRecord,
} from "~/features/businessObjects/api/recordParsers";
// eslint-disable-next-line no-restricted-imports -- same cycle as above
import { recordCollectionOptions } from "~/features/businessObjects/api/recordQueryOptions";
// eslint-disable-next-line no-restricted-imports -- same cycle as above
import { recordRef } from "~/features/businessObjects/api/recordRef";

import { parseNavPage, parseNavPageSlot } from "../pageModel/parse";
import type { NavPageRecord, NavPageSlotRecord } from "../pageModel/types";
import { fetchRecordHomeRedirect } from "./fetchRecordHomeRedirect";

export interface CreatedRecordOpenArgs {
  tenantId: string | null | undefined;
  schema: string;
  version: string;
  objectId: string;
  recordPageKey?: string;
  from?: string;
}

const pagesFrom = (items: readonly unknown[]): NavPageRecord[] => {
  const seen = new Set<string>();

  return items
    .filter((item) => !isSoftDeletedRecord(item))
    .map((item) => extractDataRecord(item))
    .filter((data): data is Record<string, unknown> => data !== null)
    .map(parseNavPage)
    .filter((item): item is NavPageRecord => item !== null)
    .filter((item) => item.enabled !== false)
    .filter((item) => {
      if (seen.has(item.key)) {
        return false;
      }

      seen.add(item.key);

      return true;
    });
};

const slotsFrom = (items: readonly unknown[]): NavPageSlotRecord[] =>
  items
    .filter((item) => !isSoftDeletedRecord(item))
    .map((item) => extractDataRecord(item))
    .filter((data): data is Record<string, unknown> => data !== null)
    .map(parseNavPageSlot)
    .filter((item): item is NavPageSlotRecord => item !== null);

const loadCatalog = async (
  queryClient: QueryClient,
  tenantId: string,
): Promise<{ pages: NavPageRecord[]; slots: NavPageSlotRecord[] }> => {
  const [pages, slots] = await Promise.all([
    queryClient.fetchQuery(recordCollectionOptions(recordRef("NavPage", "1.0.0"), tenantId)),
    queryClient.fetchQuery(recordCollectionOptions(recordRef("NavPageSlot", "1.0.0"), tenantId)),
  ]);

  return { pages: pagesFrom(pages.items), slots: slotsFrom(slots.items) };
};

/**
 * The URL a row click would open for this new record.
 *
 * A named record page is used as-is. Otherwise the record-home walk sends a
 * sample to its parent material, on the Samples tab, with the new sample
 * selected.
 */
export const hrefForCreatedRecord = async (
  queryClient: QueryClient,
  args: CreatedRecordOpenArgs,
): Promise<string> => {
  const ownPath = resolveRecordOpenPath(args.schema, args.version, undefined, args.objectId, {
    page: args.recordPageKey,
    from: args.from,
  });

  if (args.recordPageKey || !args.tenantId) {
    return ownPath;
  }

  try {
    const { pages, slots } = await loadCatalog(queryClient, args.tenantId);
    const home = await fetchRecordHomeRedirect(queryClient, {
      tenantId: args.tenantId,
      schema: args.schema,
      version: args.version,
      objectId: args.objectId,
      from: args.from,
      pages,
      slots,
    });

    return home ?? ownPath;
  } catch {
    return ownPath;
  }
};
